import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

export const SourceMonitoring = () => {
  const { toast } = useToast();

  const { data: sources, isLoading } = useQuery({
    queryKey: ['source-monitoring'],
    queryFn: async () => {
      const [sourcesResponse, healthResponse] = await Promise.all([
        supabase
          .from('ev_industry_data')
          .select('source_url, data_type, collected_at, metadata')
          .eq('is_active', true)
          .order('collected_at', { ascending: false }),
        supabase
          .from('feed_health_monitoring')
          .select('*')
          .order('last_check_time', { ascending: false })
      ]);

      if (sourcesResponse.error) {
        toast({
          variant: "destructive",
          title: "Error fetching sources",
          description: sourcesResponse.error.message
        });
        throw sourcesResponse.error;
      }

      if (healthResponse.error) {
        toast({
          variant: "destructive",
          title: "Error fetching health data",
          description: healthResponse.error.message
        });
        throw healthResponse.error;
      }

      // Merge source and health data
      const healthMap = new Map(healthResponse.data.map(h => [h.source_url, h]));
      return sourcesResponse.data.map(source => ({
        ...source,
        health: healthMap.get(source.source_url)
      }));
    },
    refetchInterval: 30000 // Refresh every 30 seconds
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Source Monitoring</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center p-4">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Active Sources</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {sources?.map((source) => (
            <div
              key={source.source_url}
              className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded"
            >
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  {source.health?.status === 'healthy' ? (
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-yellow-500" />
                  )}
                  <span className="font-medium truncate max-w-[300px]">
                    {source.source_url}
                  </span>
                </div>
                <div className="flex gap-2">
                  <Badge variant="outline">{source.data_type}</Badge>
                  {source.health && (
                    <Badge variant="secondary">
                      {source.health.items_count} items
                    </Badge>
                  )}
                </div>
              </div>
              <div className="text-sm text-muted-foreground">
                Last updated: {new Date(source.collected_at).toLocaleString()}
              </div>
            </div>
          ))}
          {!sources?.length && (
            <div className="text-center text-muted-foreground">
              No active sources found
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};